import type { DictionaryEntrySource } from '../../config/dictionaryLayer';
import type { DictionaryLanguage } from '../dictionaryFile';
import type { ExplainLevel } from '../../search/dictionaryApi';

type Translate = (key: string, params?: Record<string, unknown>) => string;

/** The prefix of every text of the dictionary tabs (`@blpde/connect`'s locale). */
export const DICTIONARY_TEXT = 'connect.settings.personalDictionary';

/** Language of a row as the tables show it: `de` → `DE`. */
export function languageLabel(lang: DictionaryLanguage): string {
  return lang.toUpperCase();
}

/** What an entry's tag says: where it comes from. */
export interface OriginedEntry {
  origin: 'own' | 'learned' | 'import';
  count?: number;
  source: DictionaryEntrySource;
}

/**
 * The tag of an entry: Firma / Mandanten-Container / geerbt for a row of another layer
 * (read-only), else eigen, gelernt (n×) — from five „gelernt (5×+)“ — or
 * importiert.
 */
export function originLabel(t: Translate, entry: OriginedEntry): string {
  if (entry.source === 'company') return t('settingsList.layer.company');
  if (entry.source === 'tenant') return t('settingsList.layer.tenant');
  if (entry.source === 'inherited') return t(`${DICTIONARY_TEXT}.origin.inherited`);
  if (entry.origin === 'learned') {
    const count = entry.count ?? 0;
    return count >= 5
      ? t(`${DICTIONARY_TEXT}.origin.learnedMax`)
      : t(`${DICTIONARY_TEXT}.origin.learned`, { count });
  }
  if (entry.origin === 'import') return t(`${DICTIONARY_TEXT}.origin.import`);
  return t(`${DICTIONARY_TEXT}.origin.own`);
}

/** The tag's colour: a learned entry stands out, the others stay quiet. */
export function originVariant(entry: OriginedEntry): 'info' | 'neutral' {
  return entry.source === 'own' && entry.origin === 'learned' ? 'info' : 'neutral';
}

/** The level of an explain row as the panel names it. */
export function explainLevelLabel(t: Translate, level: ExplainLevel['level']): string {
  return t(`${DICTIONARY_TEXT}.explain.levels.${level}`);
}

/**
 * The line under the levels of the check: what the search would offer and
 * from which level, or that it offers nothing. `undefined` before the first check.
 */
export function explainResult(t: Translate, levels: readonly ExplainLevel[], checked: boolean): string | undefined {
  if (!checked) return undefined;
  const winner = levels.find((level) => level.wins);
  const first = winner?.contributes[0]?.trim();
  if (!winner || !first) return t(`${DICTIONARY_TEXT}.explain.resultNone`);
  return t(`${DICTIONARY_TEXT}.explain.resultSuggestion`, {
    term: first,
    level: explainLevelLabel(t, winner.level),
  });
}
